import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import HeadsetMicOutlined from '@mui/icons-material/HeadsetMicOutlined';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';

import type { NavId, NavItem } from './ShellData';

/**
 * The sidebar's pages, in order. The extension and desktop items are not
 * here: they are install states, not pages (`ShellView` draws them from
 * `ShellData.extension` / `.desktop`, anchored `shell.nav.extension` and
 * `shell.nav.desktop`).
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'home', label: 'Home', icon: HomeOutlined, route: '/home', section: 'main', tour: 'shell.nav.home' },
  {
    id: 'calls',
    label: 'Calls',
    icon: HeadsetMicOutlined,
    route: '/calls',
    section: 'main',
    prerelease: true,
    tour: 'shell.nav.calls',
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    icon: ExploreOutlined,
    route: '/onboarding',
    section: 'main',
    tour: 'shell.nav.onboarding',
  },
  {
    id: 'users',
    label: 'Users',
    icon: GroupOutlined,
    route: '/settings?tab=users',
    section: 'admin',
    adminOnly: true,
    tour: 'shell.nav.users',
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: CreditCardOutlined,
    route: '/settings?tab=billing',
    section: 'admin',
    adminOnly: true,
    tour: 'shell.nav.billing',
  },
  {
    id: 'value',
    label: 'Value',
    icon: TrendingUpOutlined,
    route: '/value',
    section: 'admin',
    staffOnly: true,
    tour: 'shell.nav.value',
  },
];

/**
 * The items this viewer sees. `hiddenFeatures` is `/get_user_info`'s list, or
 * undefined until it answers: a prerelease item stays hidden until then, so a
 * customer never sees one flash in the sidebar.
 */
export function visibleNavItems(
  items: readonly NavItem[],
  viewer: { isOrgAdmin: boolean; isStaff: boolean; hiddenFeatures: readonly string[] | undefined },
): NavItem[] {
  return items.filter(
    (i) =>
      (!i.adminOnly || viewer.isOrgAdmin) &&
      (!i.staffOnly || viewer.isStaff) &&
      (!i.prerelease || (viewer.hiddenFeatures !== undefined && !viewer.hiddenFeatures.includes(i.id))),
  );
}

/** Every `data-tour` id the shell renders. */
export const SHELL_ANCHORS: readonly string[] = [
  ...NAV_ITEMS.map((i) => i.tour),
  'shell.nav.extension',
  'shell.nav.desktop',
];

/** Which item a URL lights up, or null for a page outside the nav. */
export function navIdFor(pathname: string, search: string): NavId | null {
  if (pathname === '/home') return 'home';
  if (pathname === '/calls' || pathname.startsWith('/calls/')) return 'calls';
  if (pathname === '/onboarding') return 'onboarding';
  if (pathname === '/value') return 'value';
  if (pathname === '/settings') {
    return new URLSearchParams(search).get('tab') === 'billing' ? 'billing' : 'users';
  }
  return null;
}

/**
 * Pages that are administration, not product, and so open without a seat: the
 * org settings (an admin with no seat has to be able to grant themselves one)
 * and the Dooglie staff panel and the staff Value dashboard (staff hold no seat in any org by design — see
 * `staff_lambda` on the backend, which exempts the same endpoints).
 */
export function requiresSeat(pathname: string): boolean {
  return pathname !== '/settings' && pathname !== '/admin' && pathname !== '/value';
}
